/**
 * Whether this page may start a video on its own yet.
 *
 * Every browser refuses to play audible media until the visitor has asked for
 * some, and Safari is the strict one: an unrelated click elsewhere on the page
 * is not enough for a cross-origin player. Mounting a Google Drive frame on the
 * strength of one used to leave a black box with nothing on it to press.
 *
 * So the first review carries an invitation to tap it. That invitation does not
 * intercept the tap — it cannot, because the tap has to reach the player's own
 * document to start it, and a tap spent on an overlay is a tap the visitor has
 * to make twice. It sits over the player and lets the tap through.
 *
 * Which leaves working out that it happened. A click inside a cross-origin
 * frame is invisible to us, but it moves focus off the page and onto the frame,
 * and that we can see. Any other deliberate action counts too — once the
 * visitor has touched the page at all, the browser's condition is met and the
 * invitation has nothing left to ask for.
 *
 * This deliberately does not persist. The browser's requirement resets on every
 * page load, so remembering the tap across visits would only bring back the
 * dead black player it exists to prevent.
 *
 * ⚠ What this cannot do, and no amount of code can: start a review out loud
 *   before the visitor has touched anything. Audible autoplay is refused by
 *   every browser until the page has been activated, scrolling to the video is
 *   explicitly not enough, and there is no flag, API or permission that asks
 *   for an exception. The most that is available is what happens here — be
 *   ready, and turn the sound on at the visitor's very first click, wherever on
 *   the page it lands.
 */

let unlocked = false;
const listeners = new Set();

/**
 * Events that mean the browser has actually granted the page permission.
 *
 * ⚠ Deliberately not `pointerdown` or `touchstart`, which is what this used to
 *   watch. Both of those fire at the *start* of a scroll, and a scroll is the
 *   one interaction every browser refuses to count as activation — measured in
 *   headless Chrome, 2026-08-25: three touch-scrolls produced three trusted
 *   `pointerdown`s, three `touchstart`s and two `touchend`s, and
 *   `navigator.userActivation.hasBeenActive` stayed **false** throughout. One
 *   real tap flipped it to true.
 *
 *   Believing a scroll is worse than missing it. The page then thinks it may
 *   turn the sound on, and asking an already-playing element to unmute without
 *   permission does not fail politely — the browser *pauses the video*. The
 *   review stops mid-sentence and has to be restarted muted.
 *
 *   `click` and `keydown` are activation-triggering and neither can be produced
 *   by scrolling.
 */
const GESTURES = ['click', 'keydown'];

/**
 * The browser's own answer, where it gives one.
 *
 * `navigator.userActivation` is the only direct read of the thing that actually
 * decides, so it is preferred over inferring from events. Not in every engine —
 * `null` means "no opinion available", which is not the same as `false`.
 */
const browserSaysActive = () => {
  if (typeof navigator === 'undefined' || !navigator.userActivation) return null;
  return navigator.userActivation.hasBeenActive === true;
};

export const isPlaybackUnlocked = () => unlocked;

export function unlockPlayback() {
  if (unlocked) return;
  unlocked = true;
  for (const listener of listeners) listener();
}

/**
 * Watch for the visitor's first deliberate action anywhere on the page, and for
 * focus landing inside the player — which is what a tap on a cross-origin frame
 * looks like from out here. Returns a teardown.
 *
 * Focus is the only trace that tap leaves, and no single event reports it in
 * every browser: Chrome blurs the window when a frame takes focus, Safari does
 * not — it moves `document.activeElement` and says nothing at all. Watching
 * only for the blur is why a Safari visitor who clicked Drive's own play button
 * sat there with a review playing under an invitation still asking them to
 * start it. So the frame is asked directly, on a slow poll, as well.
 *
 * `getFrame` is read at the moment of the check rather than now: the watcher is
 * attached as soon as the invitation goes up, which is a render before the
 * frame it is watching for actually exists.
 */
export function watchForFirstTap(getFrame) {
  if (unlocked || typeof window === 'undefined') return () => {};

  const onGesture = (event) => {
    // Synthetic clicks do not grant the browser's own permission either, so
    // they must not be allowed to convince us otherwise.
    if (!event.isTrusted) return;
    // And where the browser will say outright that it has not been activated,
    // believe it over the event.
    if (browserSaysActive() === false) return;
    unlockPlayback();
  };

  const checkFrameFocus = () => {
    if (browserSaysActive()) {
      unlockPlayback();
      return;
    }
    const frame = typeof getFrame === 'function' ? getFrame() : getFrame;
    if (frame && document.activeElement === frame) unlockPlayback();
  };

  // The blur arrives before focus has settled on the frame, so read it after.
  const onBlur = () => setTimeout(checkFrameFocus, 0);
  const poll = setInterval(checkFrameFocus, 250);

  for (const type of GESTURES) {
    window.addEventListener(type, onGesture, { capture: true, passive: true });
  }
  document.addEventListener('focusin', checkFrameFocus, true);
  window.addEventListener('blur', onBlur);

  return () => {
    for (const type of GESTURES) window.removeEventListener(type, onGesture, true);
    document.removeEventListener('focusin', checkFrameFocus, true);
    window.removeEventListener('blur', onBlur);
    clearInterval(poll);
  };
}

export function onPlaybackUnlock(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
